import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  ChevronLeft,
  ChevronRight,
  List,
  Check,
  Trash2,
  Edit,
  Stethoscope,
  Clock,
  User,
  FileText,
  ExternalLink,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import Select from '@/components/ui/Select';
import MedicalAppointmentForm from '@/components/MedicalAppointmentForm';
import {
  useMedicalCalendar,
  useCompleteMedicalAppointment,
  useDeleteMedicalAppointment,
  useMedicalTypes,
  useCreateMedicalType,
  useUpdateMedicalType,
  useDeleteMedicalType,
} from '@/hooks/useMedicalAppointments';
import {
  MedicalAppointment,
  MedicalAppointmentStatus,
  MedicalAppointmentCategory,
  MedicalAppointmentType,
} from '@/types';
import {
  CATEGORY_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  CATEGORY_COLORS,
} from '@/services/medicalAppointments';
import { formatPrice } from '@/utils/format';
import { useAuthStore } from '@/context/authStore';

type ViewMode = 'slots' | 'list';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
];

// Configuracion del calendario de slots
const SLOT_DURATION = 30; // minutos

// Generar slots de tiempo según el horario configurado por la veterinaria
const generateTimeSlots = (startHour: number, endHour: number) => {
  const slots: string[] = [];
  const start = Math.min(Math.max(0, startHour), 23);
  const end = Math.min(Math.max(start + 1, endHour), 24);
  for (let hour = start; hour < end; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

// Border colors for category in slot view
const CATEGORY_BORDER_COLORS: Record<MedicalAppointmentCategory, string> = {
  surgery: 'border-red-500',
  ultrasound: 'border-purple-500',
};

const CATEGORY_BG_COLORS: Record<MedicalAppointmentCategory, string> = {
  surgery: 'bg-red-50',
  ultrasound: 'bg-purple-50',
};

export default function MedicalAppointments() {
  const navigate = useNavigate();
  const { tenant } = useAuthStore();
  // Horario del calendario configurable por veterinaria (default 8-22)
  const TIME_SLOTS = useMemo(
    () => generateTimeSlots(tenant?.calendar_start_hour ?? 8, tenant?.calendar_end_hour ?? 22),
    [tenant?.calendar_start_hour, tenant?.calendar_end_hour]
  );
  const [viewMode, setViewMode] = useState<ViewMode>('slots');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [categoryFilter, setCategoryFilter] = useState<MedicalAppointmentCategory | ''>('');
  const [statusFilter, setStatusFilter] = useState<MedicalAppointmentStatus | ''>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState<MedicalAppointment | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<MedicalAppointment | null>(null);
  const [completeResult, setCompleteResult] = useState<{
    appointment: MedicalAppointment;
    serviceOrderId: number;
    total: number;
  } | null>(null);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);

  // Calcular rango de fechas segun la vista
  const { dateFrom, dateTo, days } = useMemo(() => {
    const from = new Date(currentDate);
    const to = new Date(currentDate);

    if (viewMode === 'slots') {
      // Obtener inicio de semana (lunes)
      const day = from.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      from.setDate(from.getDate() + diff);
      to.setDate(from.getDate() + 6);
    } else if (viewMode === 'list') {
      // Para lista, mostrar mes actual
      from.setDate(1);
      to.setMonth(to.getMonth() + 1);
      to.setDate(0);
    }

    const daysArray: Date[] = [];
    const current = new Date(from);
    while (current <= to) {
      daysArray.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return {
      dateFrom: from.toISOString().split('T')[0],
      dateTo: to.toISOString().split('T')[0],
      days: daysArray,
    };
  }, [currentDate, viewMode]);

  const { data: appointments = [], isLoading } = useMedicalCalendar(
    dateFrom,
    dateTo,
    categoryFilter || undefined,
  );
  const completeAppointment = useCompleteMedicalAppointment();
  const deleteAppointment = useDeleteMedicalAppointment();

  // Filtrar por estado si esta seleccionado
  const filteredAppointments = useMemo(() => {
    let filtered = appointments;
    if (statusFilter) {
      filtered = filtered.filter((apt) => apt.status === statusFilter);
    }
    return filtered;
  }, [appointments, statusFilter]);

  // Agrupar turnos por fecha
  const appointmentsByDate = useMemo(() => {
    const grouped: Record<string, MedicalAppointment[]> = {};
    filteredAppointments.forEach((apt) => {
      if (!grouped[apt.scheduled_date]) {
        grouped[apt.scheduled_date] = [];
      }
      grouped[apt.scheduled_date].push(apt);
    });
    // Ordenar por hora dentro de cada dia
    Object.values(grouped).forEach((list) => {
      list.sort((a, b) => a.scheduled_time.localeCompare(b.scheduled_time));
    });
    return grouped;
  }, [filteredAppointments]);

  // Funcion para verificar si un slot esta ocupado y obtener el turno
  const getAppointmentAtSlot = (dateStr: string, time: string): MedicalAppointment | null => {
    const dayAppointments = appointmentsByDate[dateStr] || [];
    const slotMinutes = parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);

    for (const apt of dayAppointments) {
      if (apt.status === 'cancelled') continue;

      const aptTime = apt.scheduled_time.slice(0, 5);
      const aptMinutes = parseInt(aptTime.split(':')[0]) * 60 + parseInt(aptTime.split(':')[1]);
      const aptEndMinutes = aptMinutes + apt.duration_minutes;

      // El slot esta dentro del rango del turno
      if (slotMinutes >= aptMinutes && slotMinutes < aptEndMinutes) {
        return apt;
      }
    }
    return null;
  };

  // Verificar si un slot es el inicio de un turno
  const isAppointmentStart = (dateStr: string, time: string): boolean => {
    const dayAppointments = appointmentsByDate[dateStr] || [];
    return dayAppointments.some(
      (apt) => apt.scheduled_time.slice(0, 5) === time && apt.status !== 'cancelled',
    );
  };

  // Calcular cuantos slots ocupa un turno
  const getAppointmentSlotSpan = (apt: MedicalAppointment): number => {
    return Math.ceil(apt.duration_minutes / SLOT_DURATION);
  };

  const navigateCalendar = (direction: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'slots') {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else if (viewMode === 'list') {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDateShort = (date: Date) => {
    return date.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric' });
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastSlot = (date: Date, time: string) => {
    const now = new Date();
    const slotDate = new Date(date);
    const [hours, minutes] = time.split(':').map(Number);
    slotDate.setHours(hours, minutes, 0, 0);
    return slotDate < now;
  };

  const handleNewAppointment = (date?: string, time?: string) => {
    setEditingAppointment(null);
    setSelectedDate(date || null);
    setSelectedTime(time || null);
    setIsModalOpen(true);
  };

  const handleEditAppointment = (apt: MedicalAppointment) => {
    setEditingAppointment(apt);
    setSelectedDate(null);
    setSelectedTime(null);
    setIsModalOpen(true);
  };

  const handleComplete = async (apt: MedicalAppointment) => {
    try {
      const result = await completeAppointment.mutateAsync(apt.id);
      // Mostrar modal con info de la comanda creada
      setCompleteResult({
        appointment: result.appointment,
        serviceOrderId: result.service_order_id,
        total: result.service_order_total,
      });
    } catch (error) {
      console.error('Error completing:', error);
    }
  };

  const handleGoToServiceOrder = () => {
    if (completeResult) {
      // Navegar a comandas con la comanda seleccionada para editar
      navigate(`/dashboard/service-orders?edit=${completeResult.serviceOrderId}`);
      setCompleteResult(null);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm) {
      try {
        await deleteAppointment.mutateAsync(deleteConfirm.id);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
  };

  // Calcular que slots estan cubiertos por rowSpan de turnos anteriores
  const getCoveredSlots = useMemo(() => {
    const covered: Record<string, Set<string>> = {};

    days.forEach((day) => {
      const dateStr = day.toISOString().split('T')[0];
      covered[dateStr] = new Set();

      const dayAppointments = appointmentsByDate[dateStr] || [];
      dayAppointments.forEach((apt) => {
        if (apt.status === 'cancelled') return;

        const aptTime = apt.scheduled_time.slice(0, 5);
        const [aptHour, aptMin] = aptTime.split(':').map(Number);
        const aptStartMinutes = aptHour * 60 + aptMin;
        const slotsNeeded = Math.ceil(apt.duration_minutes / SLOT_DURATION);

        // Marcar todos los slots excepto el primero como cubiertos
        for (let i = 1; i < slotsNeeded; i++) {
          const coveredMinutes = aptStartMinutes + i * SLOT_DURATION;
          const coveredHour = Math.floor(coveredMinutes / 60);
          const coveredMin = coveredMinutes % 60;
          const coveredTime = `${coveredHour.toString().padStart(2, '0')}:${coveredMin.toString().padStart(2, '0')}`;
          covered[dateStr].add(coveredTime);
        }
      });
    });

    return covered;
  }, [days, appointmentsByDate]);

  // Get slot background color based on status and category
  const getSlotColors = (appointment: MedicalAppointment) => {
    if (appointment.status === 'completed') {
      return `bg-green-100 border-l-4 ${CATEGORY_BORDER_COLORS[appointment.category]}`;
    }
    // pending
    return `${CATEGORY_BG_COLORS[appointment.category]} border-l-4 ${CATEGORY_BORDER_COLORS[appointment.category]}`;
  };

  // Renderizar vista de slots
  const renderSlotsView = () => (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 bg-gray-50 z-10 w-16 px-2 py-3 text-xs font-medium text-gray-500 border-b border-r">
                Hora
              </th>
              {days.map((day) => {
                const dateStr = day.toISOString().split('T')[0];
                return (
                  <th
                    key={dateStr}
                    className={`min-w-[140px] px-2 py-3 text-xs font-medium border-b ${
                      isToday(day) ? 'bg-blue-100 text-blue-700' : 'text-gray-500'
                    }`}
                  >
                    {formatDateShort(day)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {TIME_SLOTS.map((time, timeIndex) => (
              <tr key={time} className={timeIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                <td className="sticky left-0 bg-inherit z-10 px-2 py-0 text-xs text-gray-500 border-r font-medium text-center">
                  {time}
                </td>
                {days.map((day) => {
                  const dateStr = day.toISOString().split('T')[0];
                  const isStart = isAppointmentStart(dateStr, time);
                  const isCovered = getCoveredSlots[dateStr]?.has(time);
                  const isPast = isPastSlot(day, time);

                  // Si este slot esta cubierto por un rowSpan de un turno anterior, no renderizar
                  if (isCovered) {
                    return null;
                  }

                  // Si hay un turno que inicia en este slot, mostrarlo con rowSpan
                  if (isStart) {
                    const appointment = getAppointmentAtSlot(dateStr, time)!;
                    const slotSpan = getAppointmentSlotSpan(appointment);
                    return (
                      <td
                        key={`${dateStr}-${time}`}
                        rowSpan={slotSpan}
                        className="p-1 border border-gray-200 align-top"
                      >
                        <div
                          className={`h-full p-2 rounded cursor-pointer transition-all hover:shadow-md ${getSlotColors(appointment)}`}
                          onClick={() => handleEditAppointment(appointment)}
                          style={{ minHeight: `${slotSpan * 32 - 8}px` }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm truncate">
                              {appointment.patient_name}
                            </span>
                            <span className="text-xs text-gray-600 ml-1">
                              {appointment.duration_minutes}m
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mb-0.5">
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${CATEGORY_COLORS[appointment.category]}`}
                            >
                              {CATEGORY_LABELS[appointment.category]}
                            </span>
                          </div>
                          {appointment.type_name && (
                            <p className="text-xs text-gray-600 truncate">
                              {appointment.type_name}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 truncate">{appointment.owner_name}</p>
                          {appointment.pre_op_notes && (
                            <p className="text-xs text-amber-700 bg-amber-50 rounded px-1 py-0.5 mt-1 truncate">
                              <AlertTriangle className="inline h-3 w-3 mr-0.5" />
                              {appointment.pre_op_notes}
                            </p>
                          )}
                          {/* Acciones rapidas */}
                          {appointment.status !== 'completed' &&
                            appointment.status !== 'cancelled' && (
                              <div className="flex gap-1 mt-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleComplete(appointment);
                                  }}
                                  className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                                  title="Completar"
                                >
                                  <Check className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm(appointment);
                                  }}
                                  className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                        </div>
                      </td>
                    );
                  }

                  // Slot vacio - clickeable para crear turno (incluso si ya paso)
                  return (
                    <td
                      key={`${dateStr}-${time}`}
                      className={`p-0 border border-gray-200 h-8 transition-colors cursor-pointer ${
                        isPast ? 'bg-gray-100 hover:bg-amber-100' : 'hover:bg-green-100'
                      } ${isToday(day) && !isPast ? 'bg-blue-50/50' : ''}`}
                      onClick={() => handleNewAppointment(dateStr, time)}
                      title={isPast ? `Horario pasado — podés asignar igual a las ${time}` : `Crear turno a las ${time}`}
                    >
                      <div className="h-full w-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                        <Plus className={`h-4 w-4 ${isPast ? 'text-amber-600' : 'text-green-600'}`} />
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Renderizar tabla para vista de lista
  const renderListView = () => (
    <div className="card overflow-hidden">
      {filteredAppointments.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Turno
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Mascota / Dueno
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Notas Pre-Op
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAppointments.map((apt) => (
                <tr key={apt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div
                        className={`flex-shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                          apt.category === 'surgery'
                            ? 'bg-red-100'
                            : 'bg-purple-100'
                        }`}
                      >
                        <Stethoscope
                          className={`h-5 w-5 ${
                            apt.category === 'surgery'
                              ? 'text-red-600'
                              : 'text-purple-600'
                          }`}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {(() => {
                            const [year, month, day] = apt.scheduled_date.split('-').map(Number);
                            const localDate = new Date(year, month - 1, day);
                            return localDate.toLocaleDateString('es-AR', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                            });
                          })()}
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {apt.scheduled_time.slice(0, 5)} ({apt.duration_minutes}min)
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{apt.patient_name}</div>
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {apt.owner_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`text-xs px-2 py-1 rounded ${CATEGORY_COLORS[apt.category]}`}
                    >
                      {CATEGORY_LABELS[apt.category]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                      {apt.type_name || 'Sin tipo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[apt.status]}`}
                    >
                      {STATUS_LABELS[apt.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {apt.pre_op_notes ? (
                      <p className="text-xs text-amber-700 max-w-[200px] truncate" title={apt.pre_op_notes}>
                        {apt.pre_op_notes}
                      </p>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex justify-end gap-1">
                      {apt.status !== 'completed' && apt.status !== 'cancelled' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditAppointment(apt)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleComplete(apt)}
                            title="Completar"
                            className="text-green-600 hover:bg-green-50"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(apt)}
                        title="Eliminar"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">No hay turnos para mostrar</div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Stethoscope className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Turnos Medicos</h1>
            <p className="text-gray-600">Gestion de cirugias y ecografias</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setIsTypeModalOpen(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Gestionar Tipos
          </Button>
          <Button onClick={() => handleNewAppointment()}>
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Turno
          </Button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2">
        <button
          onClick={() => setCategoryFilter('')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            categoryFilter === ''
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Todos
        </button>
        <button
          onClick={() => setCategoryFilter('surgery')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            categoryFilter === 'surgery'
              ? 'bg-red-600 text-white shadow-sm'
              : 'bg-white text-red-700 border border-red-300 hover:bg-red-50'
          }`}
        >
          Cirugias
        </button>
        <button
          onClick={() => setCategoryFilter('ultrasound')}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            categoryFilter === 'ultrasound'
              ? 'bg-purple-600 text-white shadow-sm'
              : 'bg-white text-purple-700 border border-purple-300 hover:bg-purple-50'
          }`}
        >
          Ecografias
        </button>
      </div>

      {/* Filtros y Controles */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Navegacion de fechas */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => navigateCalendar(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => navigateCalendar(1)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
              <Button variant="secondary" size="sm" onClick={goToToday}>
                Hoy
              </Button>
            </div>
            <span className="font-medium text-gray-700">
              {viewMode === 'slots'
                ? `${days[0]?.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}`
                : currentDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
            </span>
          </div>

          <div className="flex items-center gap-4">
            {/* Filtro de estado (solo en vista lista) */}
            {viewMode === 'list' && (
              <div className="w-48">
                <Select
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as MedicalAppointmentStatus | '')}
                />
              </div>
            )}

            {/* Selector de vista */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-md">
              <button
                onClick={() => setViewMode('slots')}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  viewMode === 'slots'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Vista de horarios"
              >
                <Clock className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-primary-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Vista de lista"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Leyenda para vista de slots */}
      {viewMode === 'slots' && (
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-50 border-l-4 border-red-500 rounded"></div>
            <span className="text-gray-600">Cirugia</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-purple-50 border-l-4 border-purple-500 rounded"></div>
            <span className="text-gray-600">Ecografia</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-100 border-l-4 border-green-500 rounded"></div>
            <span className="text-gray-600">Completado</span>
          </div>
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-green-600" />
            <span className="text-gray-600">Click en slot vacio para crear turno</span>
          </div>
        </div>
      )}

      {/* Contenido segun vista */}
      {isLoading ? (
        <div className="card text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Cargando turnos...</p>
        </div>
      ) : viewMode === 'list' ? (
        renderListView()
      ) : (
        renderSlotsView()
      )}

      {/* Modal de crear/editar turno */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingAppointment(null);
          setSelectedDate(null);
          setSelectedTime(null);
        }}
        title={editingAppointment ? 'Editar Turno Medico' : 'Nuevo Turno Medico'}
        size="lg"
      >
        <MedicalAppointmentForm
          editingAppointment={editingAppointment}
          preselectedDate={selectedDate}
          preselectedTime={selectedTime}
          categoryFilter={categoryFilter || null}
          onSuccess={() => {
            setIsModalOpen(false);
            setEditingAppointment(null);
            setSelectedDate(null);
            setSelectedTime(null);
          }}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingAppointment(null);
            setSelectedDate(null);
            setSelectedTime(null);
          }}
        />
      </Modal>

      {/* Modal de confirmacion de eliminar */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Eliminar Turno"
        size="sm"
      >
        {deleteConfirm && (
          <div className="space-y-4">
            <p className="text-gray-600">
              Estas seguro de eliminar el turno de{' '}
              <strong>{deleteConfirm.patient_name}</strong> del{' '}
              {(() => {
                const [year, month, day] = deleteConfirm.scheduled_date.split('-').map(Number);
                return new Date(year, month - 1, day).toLocaleDateString('es-AR');
              })()}{' '}
              a las {deleteConfirm.scheduled_time.slice(0, 5)}?
            </p>
            <p className="text-sm text-red-600">Esta accion no se puede deshacer.</p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </Button>
              <Button variant="danger" onClick={handleDelete} loading={deleteAppointment.isPending}>
                Eliminar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de comanda creada */}
      <Modal
        isOpen={!!completeResult}
        onClose={() => setCompleteResult(null)}
        title="Turno Completado"
        size="sm"
        closeOnBackdropClick={false}
      >
        {completeResult && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <div className="flex-shrink-0">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">Procedimiento completado</p>
                <p className="text-sm text-green-600">
                  {completeResult.appointment.patient_name} -{' '}
                  {CATEGORY_LABELS[completeResult.appointment.category]}
                </p>
              </div>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-blue-800">Comanda Creada</span>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Se genero la comanda #{completeResult.serviceOrderId} lista para editar.
              </p>
              <p className="text-lg font-bold text-blue-800">
                Total: {formatPrice(completeResult.total)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Puedes agregar productos adicionales o ajustar el precio antes de cobrar.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setCompleteResult(null)}>
                Cerrar
              </Button>
              <Button onClick={handleGoToServiceOrder}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Ir a Comanda
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de gestion de tipos */}
      <Modal
        isOpen={isTypeModalOpen}
        onClose={() => setIsTypeModalOpen(false)}
        title="Gestionar Tipos de Procedimiento"
        size="lg"
      >
        <TypeManagementPanel onClose={() => setIsTypeModalOpen(false)} />
      </Modal>
    </div>
  );
}

// ---- Type Management Panel (inside the modal) ----

function TypeManagementPanel({ onClose }: { onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<MedicalAppointmentCategory>('surgery');
  const [isCreating, setIsCreating] = useState(false);
  const [editingType, setEditingType] = useState<MedicalAppointmentType | null>(null);
  const [deleteConfirmType, setDeleteConfirmType] = useState<MedicalAppointmentType | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDuration, setFormDuration] = useState('30');
  const [formPreOpNotes, setFormPreOpNotes] = useState('');

  const { data: types = [], isLoading } = useMedicalTypes(activeTab);
  const createType = useCreateMedicalType();
  const updateType = useUpdateMedicalType();
  const deleteType = useDeleteMedicalType();

  const resetForm = () => {
    setFormName('');
    setFormDescription('');
    setFormDuration('30');
    setFormPreOpNotes('');
    setIsCreating(false);
    setEditingType(null);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const startEdit = (t: MedicalAppointmentType) => {
    setFormName(t.name);
    setFormDescription(t.description || '');
    setFormDuration(String(t.default_duration_minutes));
    setFormPreOpNotes(t.default_pre_op_notes || '');
    setEditingType(t);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!formName.trim()) return;

    const durationNum = parseInt(formDuration) || 30;

    if (editingType) {
      await updateType.mutateAsync({
        id: editingType.id,
        data: {
          name: formName.trim(),
          description: formDescription.trim() || undefined,
          default_duration_minutes: durationNum,
          default_pre_op_notes: formPreOpNotes.trim() || undefined,
        },
      });
    } else {
      await createType.mutateAsync({
        name: formName.trim(),
        category: activeTab,
        description: formDescription.trim() || undefined,
        default_duration_minutes: durationNum,
        default_pre_op_notes: formPreOpNotes.trim() || undefined,
      });
    }

    resetForm();
  };

  const handleDeleteType = async () => {
    if (deleteConfirmType) {
      await deleteType.mutateAsync(deleteConfirmType.id);
      setDeleteConfirmType(null);
    }
  };

  const handleToggleActive = async (t: MedicalAppointmentType) => {
    await updateType.mutateAsync({
      id: t.id,
      data: { is_active: !t.is_active },
    });
  };

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => {
            setActiveTab('surgery');
            resetForm();
          }}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'surgery'
              ? 'bg-white text-red-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Cirugias
        </button>
        <button
          onClick={() => {
            setActiveTab('ultrasound');
            resetForm();
          }}
          className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'ultrasound'
              ? 'bg-white text-purple-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Ecografias
        </button>
      </div>

      {/* Create button */}
      {!isCreating && !editingType && (
        <Button size="sm" onClick={startCreate}>
          <Plus className="h-4 w-4 mr-1" />
          Nuevo Tipo de {activeTab === 'surgery' ? 'Cirugia' : 'Ecografia'}
        </Button>
      )}

      {/* Inline create/edit form */}
      {(isCreating || editingType) && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            {editingType ? 'Editar Tipo' : 'Nuevo Tipo'}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input
              label="Nombre"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Ej: Castración"
            />
            <Input
              label="Duración por defecto (min)"
              type="number"
              value={formDuration}
              onChange={(e) => setFormDuration(e.target.value)}
              min={5}
              max={480}
            />
          </div>
          <Input
            label="Descripción (opcional)"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder="Descripción del procedimiento"
          />
          {activeTab === 'surgery' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indicaciones pre-quirúrgicas por defecto
              </label>
              <textarea
                className="w-full px-3 py-2 border border-amber-300 bg-amber-50 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
                rows={3}
                value={formPreOpNotes}
                onChange={(e) => setFormPreOpNotes(e.target.value)}
                placeholder="Ej: Ayuno de 12 horas, suspender medicación anticoagulante..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Se cargarán automáticamente al crear un turno de este tipo.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={resetForm}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              loading={createType.isPending || updateType.isPending}
              disabled={!formName.trim()}
            >
              {editingType ? 'Guardar Cambios' : 'Crear Tipo'}
            </Button>
          </div>
        </div>
      )}

      {/* Types list */}
      {isLoading ? (
        <div className="text-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-500">Cargando tipos...</p>
        </div>
      ) : types.length > 0 ? (
        <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
          {types.map((t) => (
            <div
              key={t.id}
              className={`flex items-center justify-between px-4 py-3 ${
                !t.is_active ? 'bg-gray-50 opacity-60' : 'bg-white'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{t.name}</span>
                  {!t.is_active && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">
                      Inactivo
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {t.default_duration_minutes} min
                  </span>
                  {t.description && (
                    <span className="truncate max-w-[200px]">{t.description}</span>
                  )}
                </div>
                {t.default_pre_op_notes && (
                  <p className="text-xs text-amber-700 mt-0.5 truncate max-w-[300px]">
                    📋 {t.default_pre_op_notes}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                <button
                  onClick={() => handleToggleActive(t)}
                  className={`p-1.5 rounded text-xs transition-colors ${
                    t.is_active
                      ? 'text-yellow-600 hover:bg-yellow-50'
                      : 'text-green-600 hover:bg-green-50'
                  }`}
                  title={t.is_active ? 'Desactivar' : 'Activar'}
                >
                  {t.is_active ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  onClick={() => startEdit(t)}
                  className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Editar"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setDeleteConfirmType(t)}
                  className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Eliminar"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500 text-sm">
          No hay tipos de {activeTab === 'surgery' ? 'cirugia' : 'ecografia'} definidos.
        </div>
      )}

      {/* Delete confirmation inline */}
      {deleteConfirmType && (
        <div className="border border-red-200 rounded-lg p-4 bg-red-50 space-y-3">
          <p className="text-sm text-red-800">
            Eliminar el tipo <strong>{deleteConfirmType.name}</strong>? Los turnos existentes con
            este tipo no se veran afectados.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setDeleteConfirmType(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDeleteType}
              loading={deleteType.isPending}
            >
              Eliminar
            </Button>
          </div>
        </div>
      )}

      {/* Close button */}
      <div className="flex justify-end pt-2 border-t border-gray-200">
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </div>
    </div>
  );
}
